import { createEntityAdapter } from '@reduxjs/toolkit';
import { apiSlice } from '@/slices/apiSlice';

const workflowEventAdapter = createEntityAdapter({
	selectId: (entity) => entity._project,
	// Use _id instead of id
});

const initialState = workflowEventAdapter.getInitialState();

export const extendedApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getAllWorkflowEvents: builder.query({
			query: () => '/project/get-all-workflow-events',

			providesTags: () => [{ type: 'WorkflowEvent', id: 'LIST' }],
		}),
		getWorkflowEventForProject: builder.query({
			query: ({ _project }) => `/project/get-workflow-events/${_project}`,
			transformResponse: (responseData) => {
				// Merge the single customer into the existing list of customers
				return workflowEventAdapter.upsertMany(initialState, [
					responseData,
				]);
			},
			providesTags: (result, error, { _project }) => [
				{ type: 'WorkflowEvent', id: _project },
			],
		}),

		markWorkflowEventAsCompleted: builder.mutation({
			query: ({ _id, isCompleted }) => ({
				url: `/project/mark-workflow-event-as-completed/`,
				method: 'POST',
				body: { _id, isCompleted },
			}),
			// Optimistic update
			async onQueryStarted(
				{ _id, isCompleted },
				{ dispatch, queryFulfilled }
			) {
				// Optimistically update the getAllWorkflowEvents cache
				const patchResult = dispatch(
					extendedApiSlice.util.updateQueryData(
						'getAllWorkflowEvents',
						undefined,
						(draft) => {
							const event = draft.find(
								(event) => event._id === _id
							);
							if (event) {
								event.isCompleted = isCompleted;
							}
						}
					)
				);

				try {
					// Wait for the mutation to complete
					const { data: updatedEvent } = await queryFulfilled;

					// Update the cache with the actual server response
					dispatch(
						extendedApiSlice.util.updateQueryData(
							'getAllWorkflowEvents',
							undefined,
							(draft) => {
								const eventIndex = draft.findIndex(
									(event) => event._id === _id
								);
								if (eventIndex !== -1) {
									draft[eventIndex] = updatedEvent;
								}
							}
						)
					);
				} catch {
					// Revert the optimistic update on error
					patchResult.undo();
				}
			},
			// TODO: maybe invalidate the workflow event cache
			// invalidatesTags: (result) => [
			// 	{ type: 'WorkflowEvent', id: 'LIST' },
			// 	// Also invalidate any project-specific caches if we have the project ID
			// 	...(result?._project
			// 		? [{ type: 'WorkflowEvent', id: result._project }]
			// 		: []),
			// ],
		}),

		markAllWorkflowEventsAsCompleted: builder.mutation({
			query: () => ({
				url: `/project/mark-all-workflow-events-as-completed/`,
				method: 'POST',
			}),

			invalidatesTags: () => [{ type: 'WorkflowEvent', id: 'LIST' }],
		}),

		markWorkflowEventAsSeen: builder.mutation({
			query: ({ _id }) => ({
				url: `/project/mark-workflow-event-as-seen/`,
				method: 'POST',
				body: { _id },
			}),
			async onQueryStarted(
				{ _id },
				{ dispatch, queryFulfilled, getState }
			) {
				// Get current user ID from state
				const state = getState();
				const currentUserId = state.auth?.user?._id;

				if (!currentUserId) return;

				// Optimistically update the getAllWorkflowEvents cache
				const patchResult = dispatch(
					extendedApiSlice.util.updateQueryData(
						'getAllWorkflowEvents',
						undefined,
						(draft) => {
							const event = draft.find(
								(event) => event._id === _id
							);
							if (event) {
								// Add current user to _seenByUsers array if not already present
								if (!event._seenByUsers) {
									event._seenByUsers = [];
								}
								if (
									!event._seenByUsers.includes(currentUserId)
								) {
									event._seenByUsers.push(currentUserId);
								}
							}
						}
					)
				);

				try {
					// Wait for the mutation to complete
					const { data: updatedEvent } = await queryFulfilled;

					// Update the cache with the actual server response
					dispatch(
						extendedApiSlice.util.updateQueryData(
							'getAllWorkflowEvents',
							undefined,
							(draft) => {
								const eventIndex = draft.findIndex(
									(event) => event._id === _id
								);
								if (eventIndex !== -1) {
									draft[eventIndex] = updatedEvent;
								}
							}
						)
					);
				} catch {
					// Revert the optimistic update on error
					patchResult.undo();
				}
			},
			invalidatesTags: (result) => [
				{ type: 'WorkflowEvent', id: 'LIST' },
				...(result?._project
					? [{ type: 'WorkflowEvent', id: result._project }]
					: []),
			],
		}),
	}),
});

export const {
	useGetWorkflowEventForProjectQuery,
	useGetAllWorkflowEventsQuery,
	useMarkWorkflowEventAsCompletedMutation,
	useMarkWorkflowEventAsSeenMutation,
	useMarkAllWorkflowEventsAsCompletedMutation,
} = extendedApiSlice;

export const { util } = extendedApiSlice;
